import { Button, Icon, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ArrowNarrowRightIcon, UserGroupIcon } from '@heroicons/react/solid';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { useLocation } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import {
  CompanyEntityInfosFragment,
  FundraisingEntityWithHardCapFragment,
  FundraisingStatus,
  FundraisingType,
} from '@blockpulse3/graphql/hooks';
import { formatDate, formatNumberCurrency } from '@blockpulse3/helpers';
import { TableContainer } from '@blockpulse3/ui/commons';

dayjs.extend(LocalizedFormat);

type FundraisingListResults = (FundraisingEntityWithHardCapFragment & {
  company?: CompanyEntityInfosFragment;
})[];

type Props = {
  values: FundraisingListResults;
  onClick?: (
    id: string,
    fundraisingType: FundraisingType,
    fundraisingStatus: FundraisingStatus,
    fundraisingCompanyId?: string,
  ) => void;
};

/**
 * FundraisingTable.
 * Show fundraisings in our TableContainer component. Handle row click.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FundraisingTable({ values, onClick = noop }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nFundraisingTable = useTranslations('FundraisingTable');

  const location = useLocation();
  const isManaging: boolean = location.pathname?.includes('/manage');

  const handleFundraisingClick = (
    id: string,
    fundraisingType: FundraisingType,
    fundraisingStatus: FundraisingStatus,
    fundraisingCompanyId?: string,
  ): void => {
    onClick(id, fundraisingType, fundraisingStatus, fundraisingCompanyId);
  };

  return (
    <TableContainer maxHeight={isManaging ? 'none' : '400px'}>
      <Table variant="striped">
        <Thead>
          <Tr>
            <Th>{i18nFundraisingTable('Transaction')}</Th>
            {isManaging && <Th>{i18nFundraisingTable('CompanyName')}</Th>}
            <Th isNumeric>{i18nFundraisingTable('Amount')}</Th>
            <Th>{i18nFundraisingTable('Subscribers')}</Th>
            <Th>{i18nFundraisingTable('EndDate')}</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {values.map((fundraising) => {
            return (
              <Tr
                key={fundraising.id}
                role="button"
                onClick={(): void =>
                  handleFundraisingClick(
                    fundraising.id,
                    fundraising.type,
                    fundraising.status,
                    fundraising.company?.id,
                  )
                }
              >
                <Td>
                  <Text fontWeight="600">{fundraising.name}</Text>
                  <Text color="gray.500" fontWeight="400">
                    {i18nFundraisingTable('CapitalIncrease')}
                  </Text>
                </Td>
                {isManaging && (
                  <Td>
                    <Text fontWeight="600">{fundraising.company?.name || '-'}</Text>
                  </Td>
                )}
                <Td isNumeric fontWeight="600">
                  {fundraising.hardCap ? formatNumberCurrency(fundraising.hardCap) : '-'}
                </Td>
                <Td fontWeight="600">
                  <Stack alignItems="center" direction="row">
                    <Icon as={UserGroupIcon} boxSize="18px" color="gray.900" />
                    <Text>{fundraising.subscriptionCount}</Text>
                  </Stack>
                </Td>
                <Td fontWeight="600">
                  {fundraising.endSubscriptionDate === null
                    ? '-'
                    : formatDate(fundraising.endSubscriptionDate, 'll')}
                </Td>
                <Td textAlign="right">
                  <Button
                    rightIcon={<Icon as={ArrowNarrowRightIcon} boxSize="5" />}
                    variant="secondary"
                  >
                    {i18nGeneral('DetailsButton')}
                  </Button>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export type FundraisingTableProps = Props;
